import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { useFlags, useFlagsmith, useFlagsmithLoading } from "flagsmith/react";
import { getSfdcEnv } from "@sunrun/sales-experience-shared";
import { SfdcEnv } from "@sunrun/experience-ui-components";
import { getEnvironment } from "@sunrun/sales-experience-shared";
import { getCapSimplifiedDecision } from "../utils/evaluateCapSimplifiedExperience";
import { useCapRepRedesignDocPolling } from "../hooks/useCapRepRedesignDocPolling";
import { rollbar } from "../../providers/rollbar";
import { useAppSelector } from "../../store";
import { useCap, useCapDocs, useSow, useUserProfile } from "../hooks/useCapSwr";
import { Cap, CapDoc, CapType, Proposal, Sow } from "../capTypes";
import {
  SplatContact,
  SplatProposal,
} from "../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { useContactsData, useProposals } from "../../utils/swrHooks";
import { UserProfile } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import {
  useCheckoutAttachments,
  useOfferList,
  useProspect,
} from "../../checkout/hooks/useCheckoutSwr";
import { ListOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import type { SimplifiedExperienceIneligibleReason } from "utils/simplifiedExperienceDecision";
import { isSimplifiedPresentationFlagsReady } from "utils/isSimplifiedPresentationFlagsReady";
import { getOfferReviewSimplifiedFlagInputs } from "utils/getOfferReviewSimplifiedFlagInputs";
import { FeatureFlags } from "fixtures/features";

interface CapContextType {
  capDocsMayStillBeUpdating: boolean;
  activeProposal: Proposal | undefined;
  activeProposalDesignImages: any;
  activeProposalError: any;
  activeProposalLoading: boolean;
  cap: Cap | undefined;
  capError: any;
  capTask: any;
  contacts: SplatContact[] | undefined;
  contactsError: any;
  documents: CapDoc[] | undefined;
  documentsError: any;
  refetchCapDocuments: () => Promise<CapDoc[] | undefined>;
  equipmentLocationImages: any;
  isCapLoading: boolean;
  isCustomer: boolean;
  isContactsLoading: boolean;
  isDocumentsLoading: boolean;
  isDocumentsValidating: boolean;
  isPollingForNewCapDocs: boolean;
  returnedFromRepRedesign: boolean;
  isOfferListLoading: boolean;
  isProposalsLoading: boolean;
  isSowLoading: boolean;
  isNonSolar: boolean;
  isOnlyBattery: boolean;
  isOnlyEvCharger: boolean;
  offerListError: any;
  oneBaseUrl: string;
  originalProposal: SplatProposal | undefined;
  originalProposalDesignImages: any;
  originalProposalDesignImagesError: any;
  originalProposalDesignImagesLoading: boolean;
  proposals: SplatProposal[] | undefined;
  proposalsError: any;
  prospect: any | undefined;
  prospectError: any;
  prospectId: string | undefined;
  prospectLoading: boolean;
  reviewOfferId: string;
  sfdcUrl: string;
  sow: Sow | undefined;
  sowError: any;
  mutateSow: () => Promise<unknown>;
  hasRequestedInstallSlots: boolean;
  requestInstallSlots: () => void;
  splatBaseUrl: string;
  userProfile?: UserProfile;
  userProfileError: any;
  userProfileLoading: boolean;
  isCapSimplifiedExperience: boolean;
  capSimplifiedIneligibleReason: SimplifiedExperienceIneligibleReason | null;
  isCapSimplifiedGatePending: boolean;
}

const CapContext = createContext<CapContextType>({
  capDocsMayStillBeUpdating: false,
  activeProposal: undefined,
  activeProposalDesignImages: undefined,
  activeProposalError: undefined,
  activeProposalLoading: true,
  cap: undefined,
  capError: undefined,
  capTask: undefined,
  contacts: undefined,
  contactsError: undefined,
  documents: [],
  documentsError: undefined,
  refetchCapDocuments: async () => undefined,
  equipmentLocationImages: undefined,
  isCapLoading: true,
  isContactsLoading: true,
  isCustomer: false,
  isDocumentsLoading: true,
  isDocumentsValidating: false,
  isPollingForNewCapDocs: false,
  returnedFromRepRedesign: false,
  isOfferListLoading: true,
  isProposalsLoading: true,
  isSowLoading: true,
  isNonSolar: false,
  isOnlyBattery: false,
  isOnlyEvCharger: false,
  offerListError: undefined,
  oneBaseUrl: "",
  originalProposal: undefined,
  originalProposalDesignImages: undefined,
  originalProposalDesignImagesError: undefined,
  originalProposalDesignImagesLoading: true,
  proposals: undefined,
  proposalsError: undefined,
  prospect: undefined,
  prospectError: undefined,
  prospectId: undefined,
  prospectLoading: true,
  reviewOfferId: "",
  sfdcUrl: "",
  sow: undefined,
  sowError: undefined,
  mutateSow: async () => undefined,
  hasRequestedInstallSlots: false,
  requestInstallSlots: () => undefined,
  splatBaseUrl: "",
  userProfile: undefined,
  userProfileError: undefined,
  userProfileLoading: true,
  isCapSimplifiedExperience: false,
  capSimplifiedIneligibleReason: null,
  isCapSimplifiedGatePending: false,
});

const useCapContext = (): CapContextType => {
  const context = useContext(CapContext);
  if (!context) {
    rollbar.error(
      "[<CapContext>] -- useCapContext must be used within a CapContextProvider"
    );
    throw new Error("useCapContext must be used within a CapContextProvider");
  }
  return context;
};

const CapContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { prospectId } = useParams();
  const { pathname } = useLocation();
  const isCapPage = pathname.includes("/offer/cap");
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const cognitoToken = useAppSelector((state) => state?.auth?.cognitoToken);
  const proposalToken = useAppSelector((state) => state?.auth?.proposalToken);
  const {
    data: prospect,
    error: prospectError,
    isLoading: prospectLoading,
  } = useProspect(prospectId);
  const { cap, capError, isCapLoading } = useCap(
    authKey,
    proposalToken,
    prospectId
  );
  const capTask = cap?.salesCapTasks?.find?.(
    (task: any) => task.status !== "Inactive"
  );
  const capName = capTask?.name;
  const capLetterType = capName?.match(/\(([A-Z])\)$/)?.[1] as CapType;

  if (cap) {
    cap.type = capLetterType;
  }
  const { userProfile, userProfileError, userProfileLoading } =
    useUserProfile(authKey);

  let ENV: any = process.env.NODE_ENV ?? "";
  try {
    ENV = getEnvironment();
  } catch (e) {
    console.error("get environment error", e);
  }

  const {
    oneBaseUrl,
    salesforceBaseUrl,
    sfPartnerBaseUrl,
    sfdcEnv,
    splatBaseUrl,
  } = useMemo(() => {
    return {
      splatBaseUrl:
        ENV === "production"
          ? "https://go.sunrun.com"
          : "https://majstg-go.sunrun.com",
      oneBaseUrl:
        ENV === "production"
          ? "https://sunrunone.com"
          : "https://staging.sunrunone.com",
      salesforceBaseUrl:
        ENV === "production"
          ? "https://sunrun.my.salesforce.com"
          : "https://sunrun--uat.sandbox.my.salesforce.com",
      sfPartnerBaseUrl:
        ENV === "production"
          ? "https://sunrun.my.site.com"
          : "https://sunrun--uat.sandbox.my.site.com",
      sfdcEnv: (userProfile && getSfdcEnv(userProfile)) || "SFDC",
    };
  }, [ENV, userProfile]);

  const getSfdcBaseUrl = (
    sfdcEnv: SfdcEnv,
    sfPartnerBaseUrl: string,
    salesforceBaseUrl: string
  ): string => {
    let result = "";
    if (!sfdcEnv) return result;
    switch (sfdcEnv) {
      case "Community": {
        result = `${sfPartnerBaseUrl}/partners`;
        break;
      }
      case "D2HCommunity": {
        result = `${sfPartnerBaseUrl}/d2hpartnercommunity`;
        break;
      }
      case "SFDC":
      case "None":
      default: {
        result = salesforceBaseUrl;
        break;
      }
    }
    return result;
  };

  const { contacts, contactsError, isContactsLoading } = useContactsData(
    prospectId ?? "",
    authKey,
    proposalToken
  );
  const {
    documents,
    documentsError,
    isDocumentsLoading,
    isDocumentsValidating,
    refetchCapDocuments,
  } = useCapDocs(authKey, prospectId, proposalToken);
  const {
    capDocsMayStillBeUpdating,
    isPollingForNewCapDocs,
    returnedFromRepRedesign,
  } = useCapRepRedesignDocPolling({
    enabled: isCapPage,
    prospectId,
    refetchCapDocuments,
  });
  const { sow, sowError, isSowLoading, mutateSow } = useSow(
    authKey,
    prospectId ?? "",
    proposalToken
  );
  const activeProposal = sow?.proposal;
  const {
    data: activeProposalDesignImages,
    error: activeProposalError,
    isLoading: activeProposalLoading,
  } = useCheckoutAttachments(prospectId!, sow?.proposalName!, "Design Image");

  const [reviewOfferId, setReviewOfferId] = useState("");
  const [hasRequestedInstallSlots, setHasRequestedInstallSlots] =
    useState(false);
  const requestInstallSlots = useCallback(() => {
    setHasRequestedInstallSlots(true);
  }, []);

  const { offerList, offerListError, isOfferListLoading } = useOfferList(
    prospectId ?? "",
    cognitoToken?.token,
    proposalToken
  );

  const equipmentLocationImages = sow?.allSharinPixImages?.filter(
    (image: any) => {
      return (
        image.sharinpix__DisplayTags__c?.includes("CAP Design Summary") &&
        (image.sharinpix__DisplayTags__c
          ?.toLowerCase()
          .includes("equipment location") ||
          image.sharinpix__DisplayTags__c?.includes("Equipment") ||
          image.sharinpix__DisplayTags__c
            ?.toLowerCase()
            .includes("backup loads"))
      );
    }
  );

  useEffect(() => {
    if (prospectId && offerList && offerList.length > 0) {
      (offerList as ListOffer[]).forEach((offer) => {
        if (offer.isSigned && offer.status !== "INACTIVE") {
          setReviewOfferId(offer.id);
        }
      });
    }
  }, [offerList, prospectId]);

  const {
    data: proposals,
    error: proposalsError,
    isLoading: isProposalsLoading,
  } = useProposals(prospectId!, authKey, proposalToken);

  const findOriginalProposal = () => {
    if (proposals?.length! > 0) {
      // If there isn't any proposals, just pick the first signed one (edge case)
      const selectedProposal = proposals?.find((p: SplatProposal) => {
        return p.id === sow?.lastCustomerSignedProposalId;
      });
      return selectedProposal ?? proposals?.find((p) => p.signed);
    }
  };

  const originalProposal = findOriginalProposal();

  const {
    data: originalProposalDesignImages,
    error: originalProposalDesignImagesError,
    isLoading: originalProposalDesignImagesLoading,
  } = useCheckoutAttachments(
    prospectId!,
    originalProposal?.proposalId ?? "",
    "Design Image"
  );

  const sfdcUrl =
    getSfdcBaseUrl(sfdcEnv, sfPartnerBaseUrl, salesforceBaseUrl) ?? "";

  const isStandaloneStorage =
    activeProposal?.ProductPackage?.split(";").find(
      (element) => element === "Standalone Storage"
    ) !== undefined;

  const isStandaloneEvCharger =
    activeProposal?.ProductPackage?.split(";").find(
      (element) => element === "EV Charger; V2H; V2G"
    ) !== undefined;

  const flagsmith = useFlagsmith();
  const flagsmithLoading = useFlagsmithLoading();
  const flags = useFlags([FeatureFlags.OFFER_REVIEW_SIMPLIFIED]);
  const offerReviewSimplifiedFlag = flags[FeatureFlags.OFFER_REVIEW_SIMPLIFIED];
  const offerReviewSimplified = offerReviewSimplifiedFlag?.enabled ?? false;
  const flagsReady = isSimplifiedPresentationFlagsReady(
    flagsmith,
    flagsmithLoading
  );
  const { presentationTargets, createdDateCutoff } = useMemo(() => {
    if (!flagsReady) {
      return { presentationTargets: [], createdDateCutoff: undefined };
    }
    const flagInputs = getOfferReviewSimplifiedFlagInputs(flagsmith, flags);
    return {
      presentationTargets: flagInputs.presentationTargets,
      createdDateCutoff: flagInputs.createdDateCutoff,
    };
  }, [flagsmith, flags, flagsReady]);

  const isNonSolar =
    activeProposal?.ProductPackage === "Retrofit Storage" ||
    isStandaloneStorage ||
    isStandaloneEvCharger;

  const isOnlyBattery =
    activeProposal?.ProductPackage === "Retrofit Storage" ||
    isStandaloneStorage;

  const isCapPageLoading =
    !contacts ||
    isContactsLoading ||
    activeProposalLoading ||
    isSowLoading ||
    prospectLoading ||
    originalProposalDesignImagesLoading ||
    isCapLoading;

  const isCapSimplifiedGatePending =
    isCapPage && (!flagsReady || isCapPageLoading);

  const capSimplifiedDecision = useMemo(
    () =>
      getCapSimplifiedDecision(
        {
          isCapPage,
          isCapPageLoading,
          flagsInitialised: Boolean(flagsmith?.initialised),
          flagsReady,
        },
        {
          offerReviewSimplifiedEnabled: offerReviewSimplified,
          presentationTargets,
          createdDateCutoff,
          state: prospect?.customerState ?? undefined,
          utility: activeProposal?.utility ?? undefined,
          sow,
          activeProposal,
          cap,
          isNonSolar,
          isOnlyBattery,
          isOnlyEvCharger: isStandaloneEvCharger,
          additionalSystem: prospect?.additionalSystem,
        }
      ),
    [
      isCapPage,
      isCapPageLoading,
      flagsmith?.initialised,
      flagsReady,
      offerReviewSimplified,
      presentationTargets,
      createdDateCutoff,
      prospect?.customerState,
      prospect?.additionalSystem,
      activeProposal,
      sow,
      cap,
      isNonSolar,
      isOnlyBattery,
      isStandaloneEvCharger,
    ]
  );
  const isCapSimplifiedExperience = capSimplifiedDecision.show;
  const capSimplifiedIneligibleReason = capSimplifiedDecision.reason;

  const capContextValue = useMemo(() => {
    return {
      capDocsMayStillBeUpdating,
      activeProposal,
      activeProposalDesignImages,
      activeProposalError,
      activeProposalLoading,
      cap,
      capError,
      capTask,
      contacts,
      contactsError,
      documents,
      documentsError,
      refetchCapDocuments,
      equipmentLocationImages,
      isCapLoading,
      isContactsLoading,
      isCustomer: proposalToken && !authKey,
      isDocumentsLoading,
      isDocumentsValidating,
      isPollingForNewCapDocs,
      returnedFromRepRedesign,
      isOfferListLoading,
      isProposalsLoading,
      isSowLoading,
      isNonSolar,
      isOnlyBattery,
      isOnlyEvCharger: isStandaloneEvCharger,
      offerListError,
      oneBaseUrl,
      originalProposal,
      originalProposalDesignImages,
      originalProposalDesignImagesError,
      originalProposalDesignImagesLoading,
      proposals,
      proposalsError,
      prospect,
      prospectError,
      prospectId,
      prospectLoading,
      reviewOfferId,
      sfdcUrl,
      sow,
      sowError,
      mutateSow,
      hasRequestedInstallSlots,
      requestInstallSlots,
      splatBaseUrl,
      userProfile,
      userProfileError,
      userProfileLoading,
      isCapSimplifiedExperience,
      capSimplifiedIneligibleReason,
      isCapSimplifiedGatePending,
    } as CapContextType;
  }, [
    capDocsMayStillBeUpdating,
    activeProposal,
    activeProposalDesignImages,
    activeProposalError,
    activeProposalLoading,
    authKey,
    cap,
    capError,
    capTask,
    contacts,
    contactsError,
    documents,
    documentsError,
    refetchCapDocuments,
    equipmentLocationImages,
    isCapLoading,
    isContactsLoading,
    isDocumentsLoading,
    isDocumentsValidating,
    isPollingForNewCapDocs,
    returnedFromRepRedesign,
    isOfferListLoading,
    isProposalsLoading,
    isSowLoading,
    isNonSolar,
    isOnlyBattery,
    isStandaloneEvCharger,
    offerListError,
    oneBaseUrl,
    originalProposal,
    originalProposalDesignImages,
    originalProposalDesignImagesError,
    originalProposalDesignImagesLoading,
    proposalToken,
    proposals,
    proposalsError,
    prospect,
    prospectError,
    prospectId,
    prospectLoading,
    reviewOfferId,
    sfdcUrl,
    sow,
    sowError,
    mutateSow,
    hasRequestedInstallSlots,
    requestInstallSlots,
    splatBaseUrl,
    userProfile,
    userProfileError,
    userProfileLoading,
    isCapSimplifiedExperience,
    capSimplifiedIneligibleReason,
    isCapSimplifiedGatePending,
  ]);

  return (
    <CapContext.Provider value={capContextValue}>
      {children}
    </CapContext.Provider>
  );
};

export { useCapContext, CapContextProvider };
export type { CapContextType };
